import axios, { AxiosInstance } from 'axios';
import { ApiResponse, AuthTokens, LoginCredentials, RegisterData, AuthResponse, User, Content, ContentFilters, PaginatedResponse, ApiPagination, normalizePagination, ContentWritePayload, DashboardStats, ContentAnalytics, UpdateProfilePayload } from '../types';
import type { ContentRevision, ContentRevisionList } from '../types/revision';
import { getApiBaseUrl } from '../utils/apiConfig';
import { supabase } from '../lib/supabaseClient';
import { getAccessToken } from '../lib/authToken';
import { getErrorStatus } from '../utils/errors';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = getApiBaseUrl();
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: attach the Supabase session token (async — axios awaits it).
    this.api.interceptors.request.use(
      async (config) => {
        const token = await getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor: on 401 the Supabase session is invalid (supabase-js
    // auto-refreshes, so a 401 means truly unauthorized) → sign out + go to login.
    this.api.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        // `getErrorStatus` statt `error.response?.status`: der Fehler eines Interceptors ist
        // `unknown`, und der Handzugriff darauf las den Status ungeprüft aus einem Wert, der
        // auch ein blanker `Error` sein kann. Der Helfer prüft erst auf einen Axios-Fehler.
        if (getErrorStatus(error) === 401) {
          await supabase.auth.signOut();
          if (typeof window !== 'undefined') window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Has a valid Supabase session?
  async isAuthenticated(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  }

  // Auth API — Supabase handles identity; the backend is the source of the app User.
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    if (error || !data.session) {
      throw new Error(error?.message || 'Login failed');
    }
    const user = await this.getCurrentUser();
    const tokens: AuthTokens = {
      accessToken: data.session.access_token,
      refreshToken: data.session.refresh_token,
      expiresIn: data.session.expires_in,
    };
    return { user, tokens };
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const { data: signUp, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
    });
    if (error) {
      throw new Error(error.message || 'Registration failed');
    }
    await supabase.auth.signOut();
    const user = { email: data.email } as unknown as User;
    const tokens: AuthTokens = {
      accessToken: signUp.session?.access_token ?? '',
      refreshToken: signUp.session?.refresh_token ?? '',
      expiresIn: signUp.session?.expires_in ?? 0,
    };
    return { user, tokens };
  }

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Logout error:', error);
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/api/users/me');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get user');
  }

  /**
   * Eigenes Profil speichern (PUT /api/users/me).
   *
   * ⚠️ `email` wird bewusst NICHT mitgeschickt, obwohl die Backend-Validierung sie zuließe:
   * die E-Mail gehört Supabase Auth (GoTrue). Ein Schreiben allein in die App-Tabelle würde
   * beide Quellen auseinanderlaufen lassen — der Nutzer meldet sich weiter mit der alten
   * Adresse an, sieht im CMS aber die neue. Ein echter Wechsel braucht den GoTrue-Flow.
   *
   * `profile` ist eine JSON-Spalte und wird serverseitig ERSETZT, nicht gemischt
   * (`UserService.updateProfile` reicht das Feld direkt an Prisma weiter). Aufrufer müssen
   * den bestehenden Inhalt also selbst mitgeben.
   */
  async updateCurrentUser(payload: UpdateProfilePayload): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>('/api/users/me', payload);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Profil konnte nicht gespeichert werden');
  }

  // Content API
  async getContent(filters: ContentFilters = {}): Promise<PaginatedResponse<Content>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await this.api.get<{ success: boolean; data: { content: Content[]; pagination: ApiPagination } }>(`/api/content?${params.toString()}`);

    // Backend gibt { success: true, data: { content: [...], pagination: {...} } } zurück
    // Wir müssen es in { success: true, data: [...], pagination: {...} } umwandeln.
    // ContentService liefert die Seitenzahl als `pages` — normalizePagination() übersetzt das
    // auf `totalPages`, worauf ContentManagement.tsx seine Blätter-Leiste stützt.
    if (response.data.success && response.data.data) {
      return {
        success: true,
        data: response.data.data.content || [],
        pagination: normalizePagination(response.data.data.pagination)
      };
    }

    // Fehler-Antwort des Backends ({ success: false, ... }) enthält kein `data`.
    // Vorher wurde sie via `as any` durchgereicht, was den Aufrufer mit einem
    // Objekt statt eines Arrays zurückließ; jetzt eine leere, aber formgleiche Antwort.
    return { success: false, data: [], pagination: normalizePagination(null) };
  }

  async getContentById(id: string): Promise<Content> {
    const response = await this.api.get<ApiResponse<Content>>(`/api/content/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get content');
  }

  async createContent(data: ContentWritePayload): Promise<Content> {
    const response = await this.api.post<ApiResponse<Content>>('/api/content', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create content');
  }

  async updateContent(id: string, data: Partial<ContentWritePayload>): Promise<Content> {
    const response = await this.api.put<ApiResponse<Content>>(`/api/content/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update content');
  }

  async deleteContent(id: string): Promise<void> {
    const response = await this.api.delete<ApiResponse>(`/api/content/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete content');
    }
  }

  async publishContent(id: string): Promise<Content> {
    const response = await this.api.post<ApiResponse<Content>>(`/api/content/${id}/publish`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to publish content');
  }

  async archiveContent(id: string): Promise<Content> {
    const response = await this.api.post<ApiResponse<Content>>(`/api/content/${id}/archive`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to archive content');
  }

  /**
   * Freigeben — die Prüf-Zusage eines Inhalts.
   *
   * ⚠️ Es gibt am Backend ZWEI Freigabe-Routen, und sie tun NICHT dasselbe. Am Quelltext
   * nachgemessen, nicht angenommen:
   *
   * | Route | Kette | Wirkung |
   * |---|---|---|
   * | `POST /api/admin/content/:id/approve` | `AdminController.approveContent` → `AdminService.publishContent` → `ContentService.publishContent` | `status = PUBLISHED` **und `publishedAt = new Date()`**, Cache-Räumung, `P2025 → 404` |
   * | `POST /api/content/:id/approve` | `ContentController.approveContent` → `ContentService.updateContent({ status: 'published' })` | setzt nur den Status — **`publishedAt` bleibt `null`** |
   *
   * Verdrahtet ist die ADMIN-Route. Ein Inhalt ohne `publishedAt` ist kein Schönheitsfehler: die
   * öffentlichen Listen sortieren danach (`@@index([type, status, publishedAt])` in
   * `schema.prisma`), ein freigegebener Beitrag ohne Datum landet also an einer zufälligen
   * Stelle. Dazu prüft nur die Admin-Route die Eingabe (`AdminValidation.approveContent`) und
   * schützt sich mit `requireModeratorOrAdmin`.
   *
   * ⚠️ KEINE `{ success, data }`-Auswertung, und das ist die einzige Stelle dieser Datei ohne sie:
   * `AdminController.approveContent` antwortet mit `res.json(content)` — dem nackten Inhalt. Eine
   * Prüfung auf `response.data.success` wäre hier immer `undefined` und würde jede erfolgreiche
   * Freigabe als Fehler melden. Gemessen am Controller, nicht aus der Nachbarschaft übernommen.
   */
  async approveContent(id: string): Promise<Content> {
    const response = await this.api.post<Content>(`/api/admin/content/${id}/approve`);
    return response.data;
  }

  /**
   * Ablehnen — setzt `status = REJECTED` und legt die Begründung in `metadata.rejectionReason` ab
   * (`contentMapping.ts` leitet das Feld dorthin um; eine eigene Spalte gibt es nicht).
   *
   * ⚠️ Es gibt hierfür KEINE Admin-Route — `adminRoutes.ts` kennt nur `approve`. Der einzige Weg
   * ist `POST /api/content/:id/reject` (`authorize(['ADMIN', 'MODERATOR'])`).
   *
   * ⚠️ `begründung` ist hier PFLICHT, am Backend ist sie es NICHT: `ContentController.rejectContent`
   * liest `req.body.reason` ohne Validierungs-Middleware und lehnt ohne Begründung kommentarlos ab.
   * Die Pflicht ist eine Zusage der Oberfläche, und sie steht deshalb im Typ dieser Methode —
   * damit ein künftiger Aufrufer sie nicht versehentlich unterläuft.
   */
  async rejectContent(id: string, begründung: string): Promise<Content> {
    const response = await this.api.post<ApiResponse<Content>>(`/api/content/${id}/reject`, {
      reason: begründung,
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to reject content');
  }

  // Fassungen (content_revisions)
  //
  // ⚠️ Die Antwort ist ein OBJEKT, kein Array: `{ success, data: { revisions, pagination } }`
  // (ContentController.getContentVersions → ContentService.getContentVersions →
  // RevisionService.getRevisions). Die Umhüllung wird hier aufgelöst, damit die Ansicht nicht
  // zwei Ebenen tief greifen muss — dieselbe Rolle wie bei `getContent` darüber.
  //
  // ⚠️ Ohne Parameter, und das ist keine Auslassung: `getContentVersions` reicht serverseitig
  // `{ page: 1, limit: 50 }` FEST durch. Ein `page`-Argument hier wäre eine Zusage, die der
  // Endpunkt nicht einlöst — es würde stillschweigend ignoriert und immer dieselbe erste Seite
  // liefern. `pagination.total` kommt trotzdem mit; die Ansicht sagt hin, wenn mehr Fassungen
  // existieren als geliefert werden.
  async getContentVersions(id: string): Promise<ContentRevisionList> {
    const response = await this.api.get<ApiResponse<{ revisions?: ContentRevision[]; pagination?: ApiPagination }>>(
      `/api/content/${id}/versions`
    );
    if (response.data.success && response.data.data) {
      return {
        revisions: response.data.data.revisions || [],
        pagination: normalizePagination(response.data.data.pagination),
      };
    }
    throw new Error(response.data.message || 'Fassungen konnten nicht geladen werden');
  }

  /**
   * Setzt den Inhalt auf eine Fassung zurück und liefert den neuen Stand.
   *
   * ⚠️ Zwei Wirkungen, die der Aufrufer der Nutzerin nennen MUSS, weil sie am Namen nicht
   * ablesbar sind — beide am Backend nachgemessen (`RevisionService.restoreRevision`):
   *   1. Der aktuelle Stand wird VORHER als eigene Fassung gesichert (`createRevision(… ,
   *      ['_restored'], 'Automatischer Snapshot vor Wiederherstellung auf Version N')`). Es geht
   *      also nichts verloren.
   *   2. Das Ergebnis landet im ENTWURF: der Dienst erzwingt `status = 'DRAFT'` und
   *      `publishedAt = null`, unabhängig davon, was in der Momentaufnahme stand. Ein
   *      veröffentlichter Inhalt ist nach dem Wiederherstellen also NICHT mehr öffentlich.
   */
  async restoreContentVersion(id: string, versionId: string): Promise<Content> {
    const response = await this.api.post<ApiResponse<Content>>(
      `/api/content/${id}/versions/${versionId}/restore`
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Wiederherstellen fehlgeschlagen');
  }

  // File Upload API

  // Analytics API
  // ⚠️ Pfad korrigiert 2026-08-09: der Aufruf ging auf `/api/analytics/content/:id`, das im
  // Backend nirgends gemountet ist — jeder Aufruf lief in einen 404. Der Endpunkt existiert
  // unter `/api/content/analytics/:id` (contentRoutes.ts:99). Beim Aufräumen fiel die Methode
  // zuerst als „tot" auf; sie ist nicht tot, sie zeigte nur ins Leere.
  async getContentAnalytics(id: string): Promise<ContentAnalytics> {
    const response = await this.api.get<ApiResponse<ContentAnalytics>>(`/api/content/analytics/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get analytics');
  }

  // Admin API
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get<ApiResponse<DashboardStats>>('/api/admin/dashboard');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get dashboard stats');
  }

  // Health Check — der Body von /health wird hier nicht ausgewertet und hat aktuell
  // keinen Konsumenten; `unknown` zwingt einen späteren Aufrufer zum Narrowing.
  async healthCheck(): Promise<unknown> {
    const response = await this.api.get('/health');
    return response.data;
  }
}

// Nur der benannte Export. Das `export default apiService` daneben hatte keinen Verbraucher —
// alle acht Aufrufstellen importieren `{ apiService }` — und bot lediglich einen zweiten Namen
// für dasselbe Objekt an.
export const apiService = new ApiService();
